import React, { useMemo } from "react";
import { useInView } from "react-intersection-observer";
import styled, { useTheme } from "styled-components";
import { Flex, ImageView, ActivityIndicator, AssetType, Icon } from "@thenounproject/lingo-core";

import SquareWrapper from "./SquareWrapper";
import { Inspectable } from "@constants/Inspector";
import { getAssetError } from "@helpers/getAssetError";
import { getAssetIcon } from "@helpers/getAssetIcon";
import { getThumbnailSize } from "@components/kits/CanvasDrag/helpers";
import getAssetBackground from "@src/helpers/getAssetBackground";
import VideoThumbnail from "../GalleryAssetThumbnail/VideoThumbnail";
import AnimatedThumbnail from "../GalleryAssetThumbnail/AnimatedThumbnail";

const BadgeContainer = styled(Flex).attrs({
  background: "white",
  height: "16px",
  width: "20px",
  position: "absolute",
  borderRadius: "default",
  justifyContent: "center",
  alignItems: "center",
})`
  z-index: 99;
  top: 0px;
  left: 0px;
  box-shadow: 0px 0px 1px rgba(42, 42, 42, 0.25);
  pointer-events: none;
  overflow: hidden;
`;

function ImageAssetSquare({ inspectable: { asset, item } }: { inspectable: Inspectable }) {
  const { ref, inView } = useInView({ triggerOnce: true });
  const assetError = getAssetError(asset);
  const assetIcon = getAssetIcon(asset);
  const theme = useTheme();
  const displaySize = item.displayProperties.size;
  const thumbnailSize = getThumbnailSize(displaySize);
  const backgroundColor = useMemo(() => {
    return getAssetBackground(asset, theme);
  }, [theme, asset]);
  const autoplay = item.displayProperties?.autoplay;
  const isGif = asset?.type === AssetType.gif;
  const isLottie = asset?.type === AssetType.lottie;
  const isVideo = AssetType.videoTypes.has(asset?.type);

  function renderBadge() {
    const isSketch = AssetType.sketchTypes.has(asset?.type);
    const isModel = AssetType.modelTypes.has(asset?.type);

    let iconId;
    if (isSketch) {
      iconId = "info.sketch";
    } else if (isModel) {
      iconId = "content.model";
    } else {
      return null;
    }

    const width = 12;
    return (
      <BadgeContainer>
        <Icon {...{ width, iconId }} />
      </BadgeContainer>
    );
  }

  function renderThumbnail() {
    if (asset.meta.assetProcessing === "error" || assetError) {
      return (
        <ImageView width="100%" height="100%" icon={"info.error"} iconSize={32} iconFill="error" />
      );
    }

    if (inView && asset.meta.assetProcessing === "complete") {
      if (isVideo) {
        return (
          <VideoThumbnail
            asset={asset}
            poster={asset.thumbnails[thumbnailSize]}
            autoplay={autoplay}
          />
        );
      }

      if (isGif || isLottie) {
        return (
          <AnimatedThumbnail asset={asset} thumbnailSize={thumbnailSize} autoplay={autoplay} />
        );
      }
      return (
        <ImageView
          width="100%"
          height="100%"
          src={asset.thumbnails[thumbnailSize]}
          icon={assetIcon}
          iconSize={32}
        />
      );
    }

    return <ActivityIndicator size="small" color="gray" />;
  }

  const hasBackgroundColor = asset.meta.backgroundColor !== "clear";
  return (
    <SquareWrapper
      ref={ref}
      interactive={!autoplay && isVideo}
      p={hasBackgroundColor ? "8px" : "0px"}
      {...backgroundColor}>
      <Flex
        height="100%"
        width="100%"
        minHeight={60}
        justifyContent="center"
        alignItems="center"
        borderRadius="default"
        overflow="hidden">
        {renderBadge()}
        {renderThumbnail()}
      </Flex>
    </SquareWrapper>
  );
}

export default React.memo(ImageAssetSquare);
